import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface LightboxPhoto {
  src1600: string;
  src800: string;
  alt: string;
}

interface PhotoLightboxProps {
  photos: LightboxPhoto[];
  openIndex: number | null;
  onClose: () => void;
  onIndexChange: (index: number) => void;
}

export function PhotoLightbox({
  photos,
  openIndex,
  onClose,
  onIndexChange,
}: PhotoLightboxProps): JSX.Element | null {
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const isOpen = openIndex !== null;

  useEffect(() => {
    if (!isOpen) return;

    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      if (openIndex === null) return;
      if (e.key === 'ArrowLeft' && openIndex > 0) {
        e.preventDefault();
        onIndexChange(openIndex - 1);
        return;
      }
      if (e.key === 'ArrowRight' && openIndex < photos.length - 1) {
        e.preventDefault();
        onIndexChange(openIndex + 1);
        return;
      }
      if (e.key === 'Tab') {
        const focusables = modalRef.current?.querySelectorAll<HTMLButtonElement>(
          'button:not([disabled]):not([tabindex="-1"])',
        );
        if (!focusables || focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, openIndex, photos.length, onClose, onIndexChange]);

  useEffect(() => {
    if (!isOpen) return;

    const previousActive = document.activeElement as HTMLElement | null;
    const root = document.getElementById('root');
    root?.setAttribute('inert', '');

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    closeButtonRef.current?.focus();

    return () => {
      root?.removeAttribute('inert');
      document.body.style.overflow = previousOverflow;
      previousActive?.focus?.();
    };
  }, [isOpen]);

  if (openIndex === null) return null;
  const activePhoto = photos[openIndex];
  if (!activePhoto) return null;

  const captionId = `photo-lightbox-caption-${openIndex}`;
  const hasPrev = openIndex > 0;
  const hasNext = openIndex < photos.length - 1;

  return createPortal(
    <div
      ref={modalRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={captionId}
      className="fixed inset-0 z-50 flex items-center justify-center"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close photo viewer"
        tabIndex={-1}
        className="absolute inset-0 bg-black/90"
      />

      <p id={captionId} className="sr-only">
        Photo {openIndex + 1} of {photos.length}. {activePhoto.alt}
      </p>

      <div className="relative z-10 flex max-h-[90vh] max-w-[90vw] items-center justify-center">
        <picture>
          <source
            type="image/webp"
            srcSet={`${activePhoto.src800} 800w, ${activePhoto.src1600} 1600w`}
            sizes="90vw"
          />
          <img
            src={activePhoto.src1600}
            alt={activePhoto.alt}
            className="max-h-[90vh] max-w-[90vw] object-contain shadow-2xl"
          />
        </picture>
      </div>

      <button
        ref={closeButtonRef}
        type="button"
        onClick={onClose}
        aria-label="Close photo viewer"
        className="absolute right-4 top-4 z-10 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-black md:right-6 md:top-6"
      >
        <X size={24} aria-hidden="true" />
      </button>

      {hasPrev ? (
        <button
          type="button"
          onClick={() => onIndexChange(openIndex - 1)}
          aria-label="Previous photo"
          className="absolute left-4 top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-black md:left-6"
        >
          <ChevronLeft size={24} aria-hidden="true" />
        </button>
      ) : null}

      {hasNext ? (
        <button
          type="button"
          onClick={() => onIndexChange(openIndex + 1)}
          aria-label="Next photo"
          className="absolute right-4 top-1/2 z-10 inline-flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-black md:right-6"
        >
          <ChevronRight size={24} aria-hidden="true" />
        </button>
      ) : null}

      <p
        className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 text-sm text-white/80 md:bottom-6"
        aria-hidden="true"
      >
        {openIndex + 1} / {photos.length}
      </p>
    </div>,
    document.body,
  );
}
